// src/features/equipment/hooks/useEquipment.js
import { useState, useCallback } from 'react';
import { equipmentAPI } from '../services/equipment';
import { useApi } from '../../../hooks/api/useApi';

export function useEquipment() {
  const [equipment, setEquipment] = useState([]);
  const [currentEquipment, setCurrentEquipment] = useState(null);
  const [equipmentTasks, setEquipmentTasks] = useState([]);
  const { loading, error, execute, clearError } = useApi();

  const fetchDepartmentEquipment = useCallback(async (includeInactive = false) => {
    const result = await execute(() => 
      equipmentAPI.listEquipmentByDepartment({ include_inactive: includeInactive })
    );
    if (result.success) {
      setEquipment(result.data.equipment || []);
    }
    return result;
  }, [execute]);

  const fetchRoomEquipment = useCallback(async (roomId, includeInactive = false) => {
    const result = await execute(() => 
      equipmentAPI.listEquipmentByRoom(roomId, { include_inactive: includeInactive })
    );
    if (result.success) {
      setEquipment(result.data.equipment || []);
    }
    return result;
  }, [execute]);

  const fetchEquipmentById = useCallback(async (equipmentId) => {
    const result = await execute(() => equipmentAPI.getEquipmentById(equipmentId));
    if (result.success) {
      setCurrentEquipment(result.data);
    }
    return result;
  }, [execute]);

  const fetchEquipmentTasks = useCallback(async (equipmentId, params = {}) => {
    const result = await execute(() => equipmentAPI.listEquipmentTasks(equipmentId, params));
    if (result.success) {
      setEquipmentTasks(result.data.data?.relationships || []);
    }
    return result;
  }, [execute]);

  const createEquipment = useCallback(async (data) => {
    console.log("Creating equipment")
    const result = await execute(() => equipmentAPI.createEquipment(data));
    if (result.success) {
      await fetchDepartmentEquipment();
    }
    return result;
  }, [execute, fetchDepartmentEquipment]);

  const updateEquipment = useCallback(async (equipmentId, data) => {
    const result = await execute(() => equipmentAPI.updateEquipment(equipmentId, data));
    if (result.success) {
      setEquipment(prev => prev.map(e =>
        e.equipment_id === equipmentId ? { ...e, ...result.data } : e
      ));
      if (currentEquipment?.equipment_id === equipmentId) {
        setCurrentEquipment({ ...currentEquipment, ...result.data });
      }
    }
    return result;
  }, [execute, currentEquipment]);

  const deactivateEquipment = useCallback(async (equipmentId, reason) => {
    const result = await execute(() => equipmentAPI.deactivateEquipment(equipmentId, { reason }));
    if (result.success) {
      await fetchDepartmentEquipment(true);
    }
    return result;
  }, [execute, fetchDepartmentEquipment]);

  const reactivateEquipment = useCallback(async (equipmentId) => {
    const result = await execute(() => equipmentAPI.reactivateEquipment(equipmentId));
    if (result.success) {
      await fetchDepartmentEquipment(true);
    }
    return result;
  }, [execute, fetchDepartmentEquipment]);

  const updateReceiptControl = useCallback(async (equipmentId, controlDate) => {
    const result = await execute(() => 
      equipmentAPI.updateReceiptControl(equipmentId, { control_date: controlDate })
    );
    if (result.success) {
      await fetchDepartmentEquipment();
    }
    return result;
  }, [execute, fetchDepartmentEquipment]);

  const filterEquipment = useCallback(async (params = {}) => {
    const result = await execute(() => equipmentAPI.filterEquipment(params));
    if (result.success) {
      setEquipment(result.data.equipment || []);
    }
    return result;
  }, [execute]);

  const deleteEquipment = useCallback(async (equipmentId) => {
    const result = await execute(() => equipmentAPI.deleteEquipment(equipmentId));
    if (result.success) {
      setEquipment(prev => prev.filter(e => e.equipment_id !== equipmentId));
    }
    return result;
  }, [execute]);

  const fetchEquipmentFiles = useCallback(async (equipmentId) => {
    return await execute(() => equipmentAPI.listEquipmentFiles(equipmentId));
  }, [execute]);

  const attachFileToEquipment = useCallback(async (equipmentId, data) => {
    return await execute(() => equipmentAPI.attachEquipmentFile(equipmentId, data));
  }, [execute]);

  const removeEquipmentFile = useCallback(async (equipmentId, equipmentFileId) => {
    return await execute(() => equipmentAPI.removeEquipmentFile(equipmentId, equipmentFileId));
  }, [execute]);

  return {
    equipment,
    currentEquipment,
    equipmentTasks,
    loading,
    error,
    clearError,
    fetchDepartmentEquipment,
    filterEquipment,
    fetchRoomEquipment,
    fetchEquipmentById,
    fetchEquipmentTasks,
    createEquipment,
    updateEquipment,
    deactivateEquipment,
    reactivateEquipment,
    updateReceiptControl,
    deleteEquipment,
    fetchEquipmentFiles,
    attachFileToEquipment,
    removeEquipmentFile,
  };
}